import React, { useEffect, useState } from "react";
import { db, ref, onValue, get } from "./firebase";
import { auth } from "./firebase";
import { FiFileText, FiChevronDown, FiChevronUp, FiDownload } from "react-icons/fi";

const ExtraQuestions = () => {
    const [extraQuestions, setExtraQuestions] = useState({});
    const [enrolledCourse, setEnrolledCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeChapter, setActiveChapter] = useState(null);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const currentUser = auth.currentUser;
                if (!currentUser) {
                    setError("User not authenticated. Please log in.");
                    setLoading(false);
                    return;
                }

                const email = currentUser.email;
                let foundUser = null;
                let userCourse = null;

                // First check if user is admin/assistant in users collection
                const usersRef = ref(db, "users");
                const usersSnapshot = await get(usersRef);

                if (usersSnapshot.exists()) {
                    const usersData = usersSnapshot.val();
                    foundUser = Object.values(usersData).find(
                        (u) => u.email === email
                    );
                    if (foundUser) {
                        userCourse = foundUser.enrolledCourse;
                    }
                }

                // If not found in users collection, search in courses/students
                if (!foundUser) {
                    const coursesRef = ref(db, "courses");
                    const coursesSnapshot = await get(coursesRef);

                    if (coursesSnapshot.exists()) {
                        const courses = coursesSnapshot.val();

                        // Search through each course's students
                        for (const [courseId, courseData] of Object.entries(courses)) {
                            if (courseData.students) {
                                const student = Object.values(courseData.students).find(
                                    (s) => s.email === email
                                );
                                if (student) {
                                    foundUser = student;
                                    userCourse = courseId;
                                    break;
                                }
                            }
                        }
                    }
                }

                if (foundUser && userCourse) {
                    setEnrolledCourse(userCourse);
                } else {
                    setError("No enrolled course found. Please enroll in a course.");
                }
            } catch (error) {
                console.error("Error fetching user data:", error);
                setError("Failed to fetch user data. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, []);

    useEffect(() => {
        if (!enrolledCourse) return;

        const extraQuestionsRef = ref(db, `courses/${enrolledCourse}/extraQuestions`);
        const unsubscribeExtraQuestions = onValue(
            extraQuestionsRef,
            (snapshot) => {
                if (snapshot.exists()) {
                    const extraQuestionsData = snapshot.val();
                    const filteredExtraQuestions = {};

                    Object.entries(extraQuestionsData).forEach(([chapter, pdfs]) => {
                        // Convert pdfs to array and filter published items
                        const pdfArray = Object.values(pdfs)
                            .filter(pdf => pdf.published !== false)
                            .sort((a, b) => (a.order || 0) - (b.order || 0)); // Sort by order if available

                        if (pdfArray.length > 0) {
                            filteredExtraQuestions[chapter] = pdfArray; // Store as array
                        }
                    });

                    setExtraQuestions(filteredExtraQuestions);
                } else {
                    setExtraQuestions({});
                    setError("No extra questions found for the enrolled course.");
                }
                setLoading(false);
            },
            (error) => {
                console.error("Error fetching extra questions:", error);
                setError("Failed to fetch extra questions. Please try again later.");
                setLoading(false);
            }
        );

        return () => unsubscribeExtraQuestions();
    }, [enrolledCourse]);

    const toggleChapter = (chapter) => {
        if (activeChapter === chapter) {
            setActiveChapter(null);
        } else {
            setActiveChapter(chapter);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen px-4">
                <div className="bg-red-500/10 p-6 rounded-xl w-full max-w-md text-center border border-red-500/20">
                    <div className="text-red-400 font-medium text-lg mb-2">Error</div>
                    <p className="text-gray-300">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto px-4 py-6 w-full">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-teal-600 to-sky-600 rounded-2xl p-6 md:p-8 shadow-xl mb-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Extra Questions</h1>
                    </div>
                    <div className="flex items-center space-x-2">
                        <span className="text-teal-100 text-sm md:text-base">Total Chapters:</span>
                        <span className="text-white font-bold text-lg md:text-xl">{Object.keys(extraQuestions).length}</span>
                    </div>
                </div>
            </div>

            {/* Extra Questions List */}
            <div className="grid grid-cols-1 gap-4 md:gap-6">
                {Object.keys(extraQuestions).length > 0 ? (
                    Object.entries(extraQuestions).map(([chapter, pdfs]) => (
                        <div
                            key={chapter}
                            className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden hover:border-teal-500/30 transition-all duration-300"
                        >
                            <div className="p-6">
                                <div
                                    className="flex items-start justify-between cursor-pointer"
                                    onClick={() => toggleChapter(chapter)}
                                >
                                    <div className="flex items-start space-x-4">
                                        <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center text-teal-400">
                                            <FiFileText className="text-xl" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white mb-1">{chapter}</h3>
                                        </div>
                                    </div>
                                    <button
                                        className="text-gray-400 hover:text-white transition-colors"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleChapter(chapter);
                                        }}
                                    >
                                        {activeChapter === chapter ? (
                                            <FiChevronUp className="text-xl" />
                                        ) : (
                                            <FiChevronDown className="text-xl" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {activeChapter === chapter && (
                                <div className="px-6 pb-6">
                                    <div className="space-y-3">
                                        {pdfs.map((pdf, index) => (
                                            <a
                                                key={index}
                                                href={pdf.pdfLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center justify-between p-4 bg-gray-700/30 rounded-lg hover:bg-gray-700/50 transition-colors"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <FiFileText className="text-teal-400 text-xl" />
                                                    <span className="text-white font-medium">{pdf.pdfName}</span>
                                                </div>
                                                <FiDownload className="text-gray-400 hover:text-white transition-colors" />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                ) : (
                    <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-8 text-center">
                        <FiFileText className="mx-auto text-4xl text-gray-400 mb-4" />
                        <p className="text-gray-400 text-lg">No extra questions found</p>
                        <p className="text-gray-500 mt-2">Please check back later for updates</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExtraQuestions; 